import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import { gameplayConfig, type FoodId } from "../config/gameplay-config";
import {
  FoodPopulation,
  type FoodClusterPlan,
  type FoodEntity,
  type FoodExclusionZone,
  type FoodPosition,
} from "../simulation/food-population";
import { SeededRandom, type RandomSource } from "../simulation/seeded-random";

export interface FoodRuntimeSnapshot {
  readonly foods: readonly FoodEntity[];
}

export interface CollectedFood {
  readonly growth: number;
  readonly id: string;
}

export class FoodRuntime {
  private population: FoodPopulation | null = null;
  private spawnExclusionSource: () => readonly FoodExclusionZone[] = () => [];

  public constructor(
    flow: GameFlow,
    private seed: string,
  ) {
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public getSnapshot(): FoodRuntimeSnapshot {
    return {
      foods: this.population?.getSnapshot() ?? [],
    };
  }

  public collectOverlapping(
    position: FoodPosition,
    radius: number,
  ): readonly CollectedFood[] {
    const population = this.requirePopulation();
    const candidates = population.getSnapshot().filter((food) => {
      const x = food.position.x - position.x;
      const y = food.position.y - position.y;
      return x * x + y * y <= (food.radius + radius) ** 2;
    });
    const growthByVariant = Object.fromEntries(
      gameplayConfig.foods.map((food) => [food.id, food.growth] as const),
    ) as Readonly<Record<FoodId, number>>;
    const collected: CollectedFood[] = [];
    for (const food of candidates) {
      if (population.remove(food.id, this.spawnExclusionSource())) {
        collected.push({
          growth: growthByVariant[food.variantId],
          id: food.id,
        });
      }
    }
    return collected;
  }

  public commitDefeatDrop(plan: FoodClusterPlan): void {
    this.requirePopulation().commitCluster(plan);
  }

  public planDefeatDrop(
    position: FoodPosition,
    count: number,
    random: RandomSource,
  ): FoodClusterPlan | null {
    return this.requirePopulation().planCluster(
      position,
      count,
      {
        maximum: gameplayConfig.rewards.botFoodDropMaximumDistance,
        minimum: gameplayConfig.rewards.botFoodDropMinimumDistance,
      },
      random,
      this.spawnExclusionSource(),
    );
  }

  public remove(identifier: string): boolean {
    return this.requirePopulation().remove(
      identifier,
      this.spawnExclusionSource(),
    );
  }

  public setSpawnExclusionSource(
    source: () => readonly FoodExclusionZone[],
  ): void {
    this.spawnExclusionSource = source;
  }

  public setSeed(seed: string): void {
    this.seed = seed;
  }

  public spawnNear(position: FoodPosition, count: number): number {
    return this.requirePopulation().spawnNear(position, count);
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "results") {
      return;
    }
    if (snapshot.scene === "home") {
      this.population = null;
      return;
    }
    this.population = new FoodPopulation({
      arena: gameplayConfig.arena,
      config: gameplayConfig.foodPopulation,
      initialExclusions: [
        {
          position: gameplayConfig.movement.initialPosition,
          radius: gameplayConfig.foodPopulation.initialPlayerExclusionRadius,
        },
      ],
      random: new SeededRandom(this.seed),
      variants: gameplayConfig.foods,
    });
  }

  private requirePopulation(): FoodPopulation {
    if (this.population === null) {
      throw new Error("Food population is unavailable outside gameplay");
    }
    return this.population;
  }
}
